import React, {
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { PortalContext } from '../../context/PortalContext';
import { SearchFilterContext } from '../../context/SearchFilterContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLagoon } from '../../hooks/useLagoon';
import { useParsedProviderFilters } from '../../hooks/useProviderFilterCount';
import { useProviderFilters } from '../../hooks/useProviderFilters';
import { useSmartChoice } from '../../hooks/useSmartChoice';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import {
  LastModifiedFilter,
  ProviderFiltersType,
  ProviderSearchFilters,
} from '../../models/ProviderSearch';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { usePlanSelectorStore } from '../../store/usePlanSelectorStore';
import { returnSuppressFlag } from '../../utils/featureSuppress';
import {
  getLagoonConfigValue,
  resolveFilterSortOption,
  transformFiltersData,
} from '../../utils/providerSearch.utils';
import {
  EAP_OPTION_LABEL,
  FilterCollectionModel,
  ReverseCoverageTypesCodes,
} from '../Constants';
import { setTranslationsForPreSelectedFilters } from '../ContentFilters/AllFilterDrawer/allFilterDrawerUtil';
import {
  applyBccAutoSelectLogic,
  isFilterDisabledByBcc,
  isPrioritySchedulingActive,
} from '../ContentFilters/BccFilterUtils';
import { NameFilter } from '../ContentFilters/NameFilter';
import { getGeoLocationFromStorage } from '../PSXHeader/SearchBar/utils';
import {
  disableDistanceFilter,
  getFilterValue,
  getPreviousDistanceOrDefaultOption,
  getVirtualCareModel,
  resolveSelectedFilters,
} from '../Utils/filters.utils';

type Props = {
  sectionType: string;
  previousLocation: string;
  setPreviousLocation: (a: string) => void;
  enableMapFilterSortCTA?: boolean;
  searchResults?: any;
  suppressPremiumCarePhysician?: boolean;
};

export const ChipFilters = ({
  sectionType = '',
  previousLocation,
  setPreviousLocation,
  enableMapFilterSortCTA,
  searchResults,
  suppressPremiumCarePhysician,
}: Props) => {
  const { t } = useTranslation();
  const filtersCollection = useProviderFilters();

  const setResultsHasProviderWithUpcomingAvailability = useStore(
    StoreKeys.SET_RESULTS_HAS_PROVIDER_WITH_UPCOMING_AVAILABILITY
  );
  const ResultsHasProviderWithUpcomingAvailability = useStore(
    StoreKeys.RESULTS_HAS_PROVIDER_WITH_UPCOMING_AVAILABILITY
  );

  const { coverageType } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
    }))
  );

  const { searchFilters, setSearchFilters, defaultSearchFilters } =
    useContext(SearchFilterContext);
  const [selectedFilters, setSelectedFilters] =
    useState<ProviderSearchFilters>(searchFilters);
  const [lastModifiedFilter, setLastModifiedFilter] =
    useState<LastModifiedFilter>({});

  const allConfigs = useLagoon('config');
  const defaultSearchRadius = getLagoonConfigValue(
    allConfigs,
    'DEFAULT_SEARCH_RADIUS'
  );

  const firstRenderRef = useRef(false);
  const prevModifiedFiltersRef = useRef<string | null>(null);
  const [firstFiltersRendered, setFirstFiltersRendered] = useState<
    ProviderFiltersType[]
  >([]);

  const [
    enableMapViewEnhancements,
    enhanceDistanceFilter,
    enableBHVirtualCareFilterV2,
    enableDentalLocationNameFilter,
    enableWigsFilter,
    enableBccFilter,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
    ConstantsLagoon.FEATURE_FLAGS.ENHANCE_DISTANCE_FILTER,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_VIRTUAL_CARE_FILTER_V2,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_DENTAL_LOCATION_NAME_FILTER,
    ConstantsLagoon.FEATURE_FLAGS.WIGS_FILTER_ENABLE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BCC_FILTER,
  ]);
  const { portalData } = useContext(PortalContext);
  const planInfo = usePlanSelectorStore((state) => state.planInfo);
  const portalLockEAPfilter = portalData?.portalConfig?.lockEapFilter;

  const enabledChipFilters = getLagoonConfigValue(
    allConfigs,
    'ENABLED_CHIP_FILTERS'
  );

  const isVirtualCareFiltersSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS
      .VIRTUAL_CARE_UNET_FILTER_SUPPRESSION
  );

  const isVirtualCareUnetRailroadSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_RAILROAD
  );

  const suppressVirtualCare =
    isVirtualCareFiltersSuppressed ||
    (coverageType !== ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] &&
      isVirtualCareUnetRailroadSuppressed);

  const setUpcomingAvailability = (filters) => {
    const hasUpcomingAvailability =
      filters?.UpcomingAvailability?.options?.length > 0;
    setResultsHasProviderWithUpcomingAvailability(hasUpcomingAvailability);
  };

  const { name } = getGeoLocationFromStorage();
  const smartChoiceEligibility = useSmartChoice();
  const isSmartChoice = smartChoiceEligibility.isEnabled;

  const {
    totalCount = 0,
    providerFilterList,
    isLoading,
  } = useParsedProviderFilters({
    selectedFilters:
      previousLocation !== name ? defaultSearchFilters : selectedFilters,
  });

  useEffect(() => {
    setUpcomingAvailability(providerFilterList);
  }, [JSON.stringify(providerFilterList)]);

  useEffect(() => {
    resetFiltersState();
  }, [JSON.stringify(searchFilters), name]);

  let filteredCollection = filtersCollection;
  if (!enableMapFilterSortCTA) {
    if (enableMapViewEnhancements) {
      const enabledChipFilterList =
        (enabledChipFilters &&
          enabledChipFilters?.split(',').map((filter) => filter.trim())) ||
        [];
      if (enabledChipFilterList.length > 0) {
        filteredCollection = filtersCollection.filter(({ model }) =>
          enabledChipFilterList.includes(model)
        );
      }
    }
  }
  const bhProgramLagoonData = useLagoon('behavioral-facility-programs')();
  const filters = transformFiltersData(
    t,
    filteredCollection as ProviderFiltersType[],
    providerFilterList,
    lastModifiedFilter,
    coverageType,
    selectedFilters,
    planInfo,
    bhProgramLagoonData,
    searchResults
  );

  const resetFiltersState = () => {
    if (previousLocation !== name) {
      setSearchFilters({
        ...defaultSearchFilters,
        SortBy: searchFilters.SortBy,
      });
      setSelectedFilters({
        ...defaultSearchFilters,
        SortBy: searchFilters.SortBy,
      });
      setPreviousLocation?.(name);
    } else if (
      JSON.stringify(selectedFilters) !== JSON.stringify(searchFilters)
    ) {
      setSelectedFilters({ ...searchFilters });
    }
  };

  const onApplyFilter = (data) => {
    setLastModifiedFilter({});

    let currentSelectedFilters = {
      ...searchFilters,
      ...data,
    };

    // Apply BCC Filter Auto-Select logic if BCC filter was modified
    if (
      data[FilterCollectionModel.PRIORITY_SCHEDULING] !== undefined &&
      enableBccFilter
    ) {
      const bccFilter =
        currentSelectedFilters[FilterCollectionModel.PRIORITY_SCHEDULING];

      currentSelectedFilters = applyBccAutoSelectLogic(
        currentSelectedFilters,
        providerFilterList,
        bccFilter
      );
      // Note: 7 Days changes (ON/OFF) do NOT affect BCC - one-way dependency only
    }

    let resolvedSelectedFilters = resolveSelectedFilters(
      enhanceDistanceFilter,
      coverageType,
      currentSelectedFilters,
      searchFilters,
      enableBHVirtualCareFilterV2,
      sectionType,
      defaultSearchRadius,
      enableBccFilter
    );

    resolvedSelectedFilters = resolveFilterSortOption(
      ResultsHasProviderWithUpcomingAvailability,
      isSmartChoice,
      resolvedSelectedFilters,
      coverageType,
      t
    );
    setSearchFilters(resolvedSelectedFilters);
    setSelectedFilters(resolvedSelectedFilters);
  };

  const onFilterSelectionChange = (
    data: ProviderSearchFilters,
    lastModifiedModel: string = ''
  ) => {
    setLastModifiedFilter(
      filters.find((filter) => filter.model === lastModifiedModel) || {}
    );
    let currentSelectedFilters = {
      ...searchFilters,
      ...data,
    };

    // Apply BCC Filter Auto-Select logic if BCC filter was modified
    if (
      lastModifiedModel === FilterCollectionModel.PRIORITY_SCHEDULING &&
      enableBccFilter
    ) {
      const bccFilter =
        currentSelectedFilters[FilterCollectionModel.PRIORITY_SCHEDULING];

      currentSelectedFilters = applyBccAutoSelectLogic(
        currentSelectedFilters,
        providerFilterList,
        bccFilter
      );
    }

    const resolvedSelectedFilters = resolveSelectedFilters(
      enhanceDistanceFilter,
      coverageType,
      currentSelectedFilters,
      selectedFilters,
      enableBHVirtualCareFilterV2,
      sectionType,
      enableBccFilter,
      defaultSearchRadius
    );
    setSelectedFilters(resolvedSelectedFilters);
  };

  const onPopoverDismiss = () => {
    resetFiltersState();
  };

  const clearFilter = (model: string) => {
    const filteredData = defaultSearchFilters[model];
    const isBH =
      coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'];

    const virtualCareFilterModel = getVirtualCareModel(
      enableBHVirtualCareFilterV2,
      coverageType,
      sectionType
    );

    const isVirtualCareFilterSelected = getFilterValue(
      selectedFilters,
      virtualCareFilterModel
    );

    if (
      filteredData &&
      !(isVirtualCareFilterSelected && (isBH || enhanceDistanceFilter))
    ) {
      let updatedFilters = {
        ...searchFilters,
        [model]: filteredData,
      };
      // BCC OFF → 7 Days Auto-Deselect: When BCC chip is cleared, automatically deselect "Next 7 days"
      if (
        model === FilterCollectionModel.PRIORITY_SCHEDULING &&
        enableBccFilter
      ) {
        updatedFilters = applyBccAutoSelectLogic(
          updatedFilters,
          providerFilterList,
          false
        );
      }

      // Use resolveSelectedFilters to handle distance restoration properly
      const resolvedFilters = resolveSelectedFilters(
        enhanceDistanceFilter,
        coverageType,
        updatedFilters,
        selectedFilters,
        enableBHVirtualCareFilterV2,
        sectionType,
        Number(defaultSearchRadius)
      );

      setSearchFilters(resolvedFilters);
      setSelectedFilters(resolvedFilters);
    } else {
      let updatedSearchFilters = {
        ...searchFilters,
        // If the coverage type is Behavioral Health and a previous distance value is available,
        // set the distance filter to the previous value when the virtual care filter is cleared
        ...getPreviousDistanceOrDefaultOption(
          enhanceDistanceFilter,
          coverageType,
          model as FilterCollectionModel,
          selectedFilters,
          enableBHVirtualCareFilterV2,
          sectionType,
          Number(defaultSearchRadius)
        ),
      };
      if (updatedSearchFilters[model]) {
        delete updatedSearchFilters[model];
      }

      // BCC OFF → 7 Days Auto-Deselect: When BCC chip is cleared, automatically deselect "Next 7 days"
      if (
        model === FilterCollectionModel.PRIORITY_SCHEDULING &&
        enableBccFilter
      ) {
        updatedSearchFilters = applyBccAutoSelectLogic(
          updatedSearchFilters,
          providerFilterList,
          false
        );
      }

      // Use resolveSelectedFilters to handle distance restoration properly
      const resolvedFilters = resolveSelectedFilters(
        enhanceDistanceFilter,
        coverageType,
        updatedSearchFilters,
        selectedFilters,
        enableBHVirtualCareFilterV2,
        sectionType,
        Number(defaultSearchRadius)
      );

      setSearchFilters(resolvedFilters);
      setSelectedFilters(resolvedFilters);
    }
    if (FilterCollectionModel.EAP_OFFERED === model) {
      onFilterSelectionChange(
        {
          ...searchFilters,
          [FilterCollectionModel.AREA_OF_EXPERTISE]:
            searchFilters.AreaOfExpertise?.filter(
              (option) => option?.dnt_label !== EAP_OPTION_LABEL
            ),
        },
        model
      );
      setSearchFilters({
        ...searchFilters,
        [FilterCollectionModel.AREA_OF_EXPERTISE]:
          searchFilters.AreaOfExpertise?.filter(
            (option) => option?.dnt_label !== EAP_OPTION_LABEL
          ),
        [model]: [],
      });
    } else if (FilterCollectionModel.AREA_OF_EXPERTISE === model) {
      onFilterSelectionChange(
        {
          ...searchFilters,
          [model]: searchFilters.AreaOfExpertise?.filter(
            (option) => option?.dnt_label === EAP_OPTION_LABEL
          ),
        },
        model
      );
      setSearchFilters({
        ...searchFilters,
        [model]: searchFilters.AreaOfExpertise?.filter(
          (option) => option?.dnt_label === EAP_OPTION_LABEL
        ),
      });
    }
  };

  const isVirtualCareFilterSelected = getFilterValue(
    selectedFilters,
    FilterCollectionModel.VIRTUAL_CARE
  );

  const filteredFilters = filters
    .map((filter) => {
      if (
        disableDistanceFilter(
          filter.model as FilterCollectionModel,
          isVirtualCareFilterSelected,
          selectedFilters,
          enhanceDistanceFilter
        )
      ) {
        filter.isDisabled = true;
      }

      return filter;
    })
    .filter(
      ({ chipFilters, model }) =>
        chipFilters[coverageType]?.includes(sectionType) &&
        model !== FilterCollectionModel.CULTURAL_COMPETENCE
    )
    .filter(({ model }) => {
      if (
        model === FilterCollectionModel.LOCATION_NAME &&
        !enableDentalLocationNameFilter
      ) {
        return false;
      } else {
        return true;
      }
    });

  const resolvedPreselectedFilters = setTranslationsForPreSelectedFilters(
    filters,
    selectedFilters
  );

  let modifiedfilteredFilters: ProviderFiltersType[] = filteredFilters;
  const eapFilter = filteredFilters.find(
    (filter) => filter.model === FilterCollectionModel.EAP_OFFERED
  );
  if (eapFilter) {
    modifiedfilteredFilters = filteredFilters.filter(
      (item) => item.model !== FilterCollectionModel.SPECIALTY_CODE
    );
  }
  modifiedfilteredFilters = modifiedfilteredFilters.map((filter) => {
    if (filter.model === FilterCollectionModel.HEALTH_SUPPLIES_OFFERED) {
      const updatedOptions = filter.options.filter((option) => {
        if (
          option.label === 'Wigs' &&
          option.value === '853' &&
          (enableWigsFilter === undefined ||
            enableWigsFilter === null ||
            !enableWigsFilter)
        ) {
          return false;
        } else {
          return true;
        }
      });
      filter.options = updatedOptions;
      return filter;
    }
    return filter;
  });

  useEffect(() => {
    const currentFilters = JSON.stringify(modifiedfilteredFilters ?? {});
    const prevFiltersSignature = prevModifiedFiltersRef.current;

    // On initial mount, capture baseline but do not set the flag nor state yet
    if (prevFiltersSignature === null) {
      prevModifiedFiltersRef.current = currentFilters;
      return;
    }

    // Detect first actual change since mount
    const hasChanged = currentFilters !== prevFiltersSignature;
    if (hasChanged && !firstRenderRef.current) {
      firstRenderRef.current = true;
      setFirstFiltersRendered(modifiedfilteredFilters);
    }
  }, [modifiedfilteredFilters]);

  return (
    <Fragment>
      {modifiedfilteredFilters
        .filter((filter) => {
          if (
            filter.model !== FilterCollectionModel.VIRTUAL_CARE_OPTIONS ||
            !suppressVirtualCare
          ) {
            return filter;
          }

          return null;
        })
        .filter((filter) => {
          if (
            filter.model !== FilterCollectionModel.PREMIUM_CARE_PHYSICIAN ||
            !suppressPremiumCarePhysician
          ) {
            return filter;
          }

          return null;
        })
        .sort(
          (a, b) =>
            a.chipFiltersOrder[coverageType] - b.chipFiltersOrder[coverageType]
        )
        .map(({ label, title, options, model, type, isDisabled = false }) => {
          // Check if Priority Scheduling is active
          const isBccActive = isPrioritySchedulingActive(searchFilters);

          // Dynamically disable filters when Priority Scheduling is active
          const isDynamicallyDisabled = isFilterDisabledByBcc(
            model,
            isBccActive
          );

          return (
            <NameFilter
              clearFilter={clearFilter}
              defaultValue={resolvedPreselectedFilters[model]}
              enableMapViewEnhancements={enableMapViewEnhancements}
              firstFiltersRendered={firstFiltersRendered}
              isDisabled={
                isDisabled ||
                isDynamicallyDisabled ||
                (portalLockEAPfilter === true &&
                  model === FilterCollectionModel.EAP_OFFERED)
              }
              isLoading={isLoading}
              key={label}
              label={label}
              model={model}
              onApplyFilter={onApplyFilter}
              onFilterSelectionChange={onFilterSelectionChange}
              onPopoverDismiss={onPopoverDismiss}
              options={options}
              sectionType={sectionType}
              title={title || label}
              totalCount={totalCount}
              type={type}
            />
          );
        })}
    </Fragment>
  );
};
